import type { ReactNode } from 'react';
import React, { useContext, createContext, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import type { OrganizationJobDetails, User } from 'state/remote/types';
import useJobDetails from 'state/remote/remote-organization/use-job-details';
import useUserDetails from 'state/remote/remote-organization/use-user-details';

type OrganisationJobDetailContext = {
  jobContext: {
    data: OrganizationJobDetails;
    isLoading?: boolean;
    error: string | null;
  };
  userContext: {
    data: User;
    isLoading?: boolean;
    error: string | null;
  };
};

const organisationJobDetailContextInitialValue = {
  jobContext: {
    data: {} as OrganizationJobDetails,
    error: null,
  },
  userContext: {
    data: {} as User,
    error: null,
  },
};

const OrganisationJobDetailCtx = createContext<OrganisationJobDetailContext>(
  organisationJobDetailContextInitialValue,
);

const OrganisationJobDetailContextProvider: React.FC<{
  children: ReactNode;
}> = ({ children }) => {
  const { id } = useParams();

  const jobId: number = parseInt(id ?? '', 10);

  const jobContext = useJobDetails(jobId);
  const userContext = useUserDetails(
    jobContext.data.assigned_to?.id,
    jobContext.data.id,
    'job_ad',
  );

  const organisationJobDetailContext =
    useMemo((): OrganisationJobDetailContext => {
      return {
        jobContext,
        userContext,
      };
    }, [jobContext, userContext]);

  return (
    <OrganisationJobDetailCtx.Provider value={organisationJobDetailContext}>
      {children}
    </OrganisationJobDetailCtx.Provider>
  );
};

export const useOrganisationJobDetail = (): OrganisationJobDetailContext =>
  useContext(OrganisationJobDetailCtx);

export default {
  Consumer: OrganisationJobDetailCtx.Consumer,
  Provider: OrganisationJobDetailContextProvider,
};
